import { useMemo } from 'react';
import type { ReactElement } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  systemValueActionGetCacheOptions,
  systemValueActionQueryAllOptions,
  systemValueSaveMutation,
} from '@quoruum/frontend-common/oas/gen/admin/@tanstack/react-query.gen';
import type { SystemValueBean } from '@quoruum/frontend-common/oas/gen/admin/types.gen';
import { queryClient } from '@quoruum/frontend-common/api/queryClient';
import {
  getApiErrorMessage,
  getProblemDetailMessage,
} from '@quoruum/frontend-common/api/errorMessage';
import { EditableEntityTable } from '../components/EditableEntityTable';
import type {
  EditableFieldSpec,
  EditableFieldValue,
  EntityDetailRow,
} from '../components/EditableEntityTable';

type EditableSystemFieldKey = string;
type SaveSystemFieldKey = NonNullable<SystemValueBean['key']>;
type EditableSystemFieldType = 'BOOLEAN' | 'INT';

function formatValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '-';
  }

  if (typeof value === 'string') {
    return value || '-';
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  try {
    return JSON.stringify(value);
  } catch {
    return '[unserializable]';
  }
}

function hasSystemKey(
  value: SystemValueBean
): value is SystemValueBean & { key: NonNullable<SystemValueBean['key']> } {
  return value.key !== undefined;
}

function toEditableSystemType(type: unknown): EditableSystemFieldType | null {
  if (type === 'BOOLEAN' || type === 'INT') {
    return type;
  }

  return null;
}

function parseBooleanValue(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  if (typeof value === 'string') {
    return value.trim().toLowerCase() === 'true';
  }

  return false;
}

function parseIntValue(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.trunc(value);
  }

  if (typeof value === 'string') {
    const parsed = Number(value.trim());
    if (Number.isFinite(parsed)) {
      return Math.trunc(parsed);
    }
  }

  return 0;
}

export function SystemValues(): ReactElement {
  const allSystemValuesQuery = useQuery(systemValueActionQueryAllOptions());
  const cacheSystemValuesQuery = useQuery(systemValueActionGetCacheOptions());
  const createSaveSystemValueMutation = systemValueSaveMutation;
  const saveSystemValueMutation = useMutation(createSaveSystemValueMutation());

  const { rows, editableFieldSpecs, editableFieldTypeByKey } = useMemo(() => {
    const cacheByKey = new Map(
      (cacheSystemValuesQuery.data ?? []).filter(hasSystemKey).map((item) => [item.key, item.value])
    );

    const fieldSpecs = new Map<EditableSystemFieldKey, EditableFieldSpec>();
    const fieldTypeByKey = new Map<EditableSystemFieldKey, EditableSystemFieldType>();

    const mappedRows = [...(allSystemValuesQuery.data ?? [])]
      .filter(hasSystemKey)
      .sort((a, b) => a.key.localeCompare(b.key))
      .map((item) => {
        const cacheValue = cacheByKey.get(item.key);
        const row: EntityDetailRow<EditableSystemFieldKey> = {
          id: String(item.key),
          key: String(item.key),
          value: `${formatValue(item.value)} (Cache: ${formatValue(cacheValue)})`,
        };

        const editableType = toEditableSystemType(item.type);
        if (editableType !== null) {
          row.editableFieldKey = item.key;
          fieldTypeByKey.set(item.key, editableType);

          if (editableType === 'BOOLEAN') {
            row.editableFieldBooleanValue = parseBooleanValue(item.value);
            fieldSpecs.set(item.key, { editType: 'checkbox' });
          } else {
            row.editableFieldNumberValue = parseIntValue(item.value);
            fieldSpecs.set(item.key, {
              editType: 'number',
              parseAsInteger: true,
              step: 1,
            });
          }
        }

        return row;
      });

    return {
      rows: mappedRows,
      editableFieldSpecs: fieldSpecs,
      editableFieldTypeByKey: fieldTypeByKey,
    };
  }, [allSystemValuesQuery.data, cacheSystemValuesQuery.data]);

  async function handleSaveSystemField(
    fieldKey: EditableSystemFieldKey,
    value: EditableFieldValue
  ): Promise<void> {
    const fieldType = editableFieldTypeByKey.get(fieldKey);
    if (!fieldType) {
      return;
    }

    let parsedValue: unknown;
    if (fieldType === 'BOOLEAN') {
      if (typeof value !== 'boolean') {
        throw new Error('Expected a boolean value.');
      }
      parsedValue = value;
    } else {
      if (typeof value !== 'number' || !Number.isFinite(value) || !Number.isInteger(value)) {
        throw new Error('Expected a whole number value.');
      }
      parsedValue = value;
    }

    try {
      await saveSystemValueMutation.mutateAsync({
        path: {
          key: fieldKey as SaveSystemFieldKey,
        },
        body: {
          key: fieldKey as SaveSystemFieldKey,
          type: fieldType,
          value: parsedValue,
        },
      });

      await Promise.all([
        queryClient.fetchQuery(systemValueActionQueryAllOptions()),
        queryClient.fetchQuery(systemValueActionGetCacheOptions()),
      ]);
    } catch (error) {
      let message = 'Unable to save system value.';
      const detail = getProblemDetailMessage(error);
      if (detail) {
        message = `Unable to save system value. ${detail}`;
      }
      const wrappedError = new Error(message) as Error & { cause?: unknown };
      wrappedError.cause = error;
      throw wrappedError;
    }
  }

  const errorMessage = allSystemValuesQuery.isError
    ? getApiErrorMessage(allSystemValuesQuery.error, 'Unable to load system values.')
    : cacheSystemValuesQuery.isError
      ? getApiErrorMessage(cacheSystemValuesQuery.error, 'Unable to load system values.')
      : null;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-900">System Values</h1>
      <p className="mt-2 text-sm text-slate-600">
        Current system values from the database and cache.
      </p>

      <div className="mt-6 overflow-hidden rounded-md border border-slate-200">
        {allSystemValuesQuery.isLoading || cacheSystemValuesQuery.isLoading ? (
          <p className="px-3 py-2 text-xs text-slate-600">Loading system values...</p>
        ) : errorMessage ? (
          <p className="px-3 py-2 text-xs text-rose-700">{errorMessage}</p>
        ) : (
          <EditableEntityTable
            rows={rows}
            fieldDefinitions={editableFieldSpecs}
            onSave={handleSaveSystemField}
            keyColumnWidth="content"
          />
        )}
      </div>
    </section>
  );
}
